import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  CheckCircle2,
  Heart,
  Milk,
  Apple,
  ShieldCheck,
  Award,
  ArrowRight,
  UtensilsCrossed,
  MapPin,
} from "lucide-react";
import { businessInfo } from "../data/business";

export default function About() {
  return (
    <div className="space-y-16 sm:space-y-24 pb-16">
      {/* HEADER HERO */}
      <section className="bg-gradient-to-b from-sky-50/60 to-transparent pt-10 sm:pt-16 pb-8 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-sky-200 text-sky-800 text-xs sm:text-sm font-bold shadow-xs">
            <Sparkles className="w-4 h-4 text-sky-600" />
            <span>Our Heritage & Philosophy</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2C3E50] tracking-tight">
            Crafting Udaipur's Truest <br />
            <span className="text-sky-600">Natural Ice Cream</span>
          </h1>

          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Real seasonal fruits. Fresh whole milk. No synthetic colors. No chemical stabilizers. Just pure, honest indulgence handcrafted daily in Udaipur.
          </p>
        </div>
      </section>

      {/* STORY & INSPIRATION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          
          {/* IMAGE GRID (5 Cols) */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <img
                src="https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=600&auto=format&fit=crop&q=80"
                alt="Real Mango Ice Cream"
                className="w-full h-52 object-cover rounded-3xl shadow-md"
              />
              <img
                src="https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=600&auto=format&fit=crop&q=80"
                alt="Traditional Rabdi"
                className="w-full h-40 object-cover rounded-3xl shadow-md"
              />
            </div>
            <div className="space-y-4 pt-8">
              <img
                src="https://images.unsplash.com/photo-1560008581-09826d1de69e?w=600&auto=format&fit=crop&q=80"
                alt="Kesar Pista Ice Cream"
                className="w-full h-40 object-cover rounded-3xl shadow-md"
              />
              <img
                src="https://images.unsplash.com/photo-1551024506-0bccd828d307?w=600&auto=format&fit=crop&q=80"
                alt="Waffle Cones Baked Fresh"
                className="w-full h-52 object-cover rounded-3xl shadow-md"
              />
            </div>
          </div>

          {/* STORY CONTENT (7 Cols) */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-600">
              The HimCream Story
            </span>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50] tracking-tight">
              Why We Refuse to Make "Commercial" Ice Cream
            </h2>

            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Most commercially manufactured frozen desserts today contain vegetable oils, artificial flavor syrups, synthetic colors, and heavy aerated overrun that leaves little substance.
            </p>

            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              At **HimCream Naturals**, we took the traditional path. We source pure buffalo and cow milk from local dairy farmers in Rajasthan and blend it with 100% real seasonal fruits — Alphonso mangoes, strawberries, tender coconuts, and Kashmiri saffron.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-2xl bg-white border border-stone-200/80 shadow-xs flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                  <Milk className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#2C3E50]">Pure Dairy Milk</h4>
                  <p className="text-xs text-stone-500 mt-0.5">Never reconstituted milk powder or vegetable fats.</p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white border border-stone-200/80 shadow-xs flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                  <Apple className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-[#2C3E50]">100% Real Fruits</h4>
                  <p className="text-xs text-stone-500 mt-0.5">Handpicked seasonal fruits crushed in small batches.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-sky-600 mb-2">
            The Difference
          </h2>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-[#2C3E50]">
            HimCream Naturals vs Commercial Ice Creams
          </h3>
        </div>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-200">
            {/* HIMCREAM */}
            <div className="p-6 sm:p-8 bg-sky-50/40 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold text-sm">
                  ✓
                </div>
                <h4 className="text-lg font-extrabold text-[#2C3E50]">
                  HimCream Naturals
                </h4>
              </div>

              <ul className="space-y-3 text-sm text-stone-700">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span><strong>100% Real Fruits</strong> sourced fresh in season</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span><strong>Pure Farm Milk</strong> with rich natural dairy cream</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span><strong>Zero Artificial Essence</strong> or synthetic food dyes</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span><strong>Crafted Daily in Small Batches</strong> in Udaipur</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
                  <span>Authentic <strong>Royal Faluda & Slow-Simmered Rabdi</strong></span>
                </li>
              </ul>
            </div>

            {/* COMMERCIAL */}
            <div className="p-6 sm:p-8 bg-stone-50/60 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-stone-300 text-stone-700 flex items-center justify-center font-bold text-sm">
                  ✕
                </div>
                <h4 className="text-lg font-bold text-stone-500">
                  Commercial Frozen Desserts
                </h4>
              </div>

              <ul className="space-y-3 text-sm text-stone-500">
                <li className="flex items-start gap-2.5">
                  <span className="text-rose-500 font-bold shrink-0">✕</span>
                  <span>Synthetic fruit essences and artificial food colors</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-rose-500 font-bold shrink-0">✕</span>
                  <span>Hydrogenated vegetable oils & palm fat substitutes</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-rose-500 font-bold shrink-0">✕</span>
                  <span>Excess chemical emulsifiers & stabilizers</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-rose-500 font-bold shrink-0">✕</span>
                  <span>Mass produced months in advance in large warehouses</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-rose-500 font-bold shrink-0">✕</span>
                  <span>High air overrun to artificially inflate package volume</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-3xl p-8 sm:p-12 text-white text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
          <div className="space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold">
              Ready to Experience Real Natural Ice Cream?
            </h3>
            <p className="text-sky-100 text-sm sm:text-base max-w-xl">
              Browse our complete menu of artisanal scoops, thick shakes, royal faludas, and fresh rabdi.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/menu"
              className="bg-white hover:bg-stone-50 text-sky-700 font-bold text-sm px-6 py-3 rounded-full shadow transition"
            >
              Explore Menu
            </Link>
            <Link
              to="/select-table"
              className="bg-sky-700 hover:bg-sky-800 text-white font-bold text-sm px-6 py-3 rounded-full shadow transition"
            >
              Order at Table
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
